<script setup lang="ts">
import type { UpdateUserEmail } from '~/types/UserData';

type Props = {
  opacity?: boolean;
  isEmailUpdateEnabled?: boolean;
  allowGrow?: boolean;
};

const emit = defineEmits(['check-pendency']);

const {
  opacity = false,
  isEmailUpdateEnabled = false,
  allowGrow = false,
} = defineProps<Props>();

const route = useRoute();
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { userEmail, fetchUserProfile } = useUserData();
const { updateUserEmail } = useUserDataStore();
const { isEmailVerified } = useUserValidations();
const { resetEmailToChange } = useUserActivityStore();
const { isModuleNeededOnFirstWithdraw } = useModulesValidation();

const isLoading = ref<boolean>(false);
const isEditing = ref<boolean>(false);
const formEmail = ref<string>('');

const hasPendency = computed(() => {
  return !isEmailVerified.value;
});

const isUpdateEmailAllowed = computed(() => {
  return isEmailUpdateEnabled && isEmailVerified.value;
});

const isEmailEqual = computed(() => userEmail.value === formEmail.value);

const onSubmitEmailVerify = async () => {
  resetEmailToChange();

  await $containment.handle(
    [
      {
        name: 'email',
        force: true,
      },
    ],
    () => {
      openToaster({
        message: t('account.email_section.verify_success'),
        type: 'success',
      });
      isEditing.value = false;
      emit('check-pendency');
    },
  );
};

async function handleEditEmail() {
  if (!formEmail.value) {
    openToaster({
      message: t('account.email_section.empty'),
      type: 'error',
    });

    return;
  }

  if (isEmailEqual.value) {
    openToaster({
      message: `${t('general.email')} ${t('account.verified')}`,
      type: 'warning',
    });

    return;
  }

  await $containment.handle('update_data', () => {
    updateEmail();
  });
}

async function solveEmailContainment() {
  await $containment.handle(
    [
      {
        name: 'email',
        force: true,
      },
    ],
    async () => {
      await nextTick(() => {
        isEditing.value = false;
        emit('check-pendency');
      });

      openToaster({
        message: t('account.user_data.update_success'),
        type: 'success',
      });
    },
  );
}

const updateEmail = () => {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  const payload: UpdateUserEmail = {
    email: formEmail.value,
  };

  if ($kyc.current.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  isLoading.value = true;

  updateUserEmail(payload)
    .then(async (response) => {
      const accessToken = response.data.access_token;

      if (accessToken) {
        const { saveToken } = useJwtStore();

        saveToken(accessToken);
        await fetchUserProfile({ force: true });
      }
      await solveEmailContainment();
    })
    .catch((error) => {
      const message =
        error?.data?.message || error?.message || t('general.error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const toggleEdit = () => {
  if (isEditing.value) {
    resetEmailToChange();
  }

  isEditing.value = !isEditing.value;
};

defineExpose({
  name: 'email',
  isEditing,
  hasPendency,
  toggleEdit,
});

onMounted(() => {
  if (route.query?.highlight === 'email' && hasPendency.value) {
    isEditing.value = true;
  }
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div
      :class="[
        $style.container,
        { [$style.opacity]: opacity, [$style.grow]: allowGrow && !isEditing },
      ]"
    >
      <UserProfileDataCard
        :action-label="
          isUpdateEmailAllowed
            ? isEditing
              ? $t('general.cancel')
              : $t('general.edit')
            : undefined
        "
        :action-type="!isEditing ? 'edit' : undefined"
        :badge-label="
          isEmailVerified ? $t('account.verified') : $t('account.not_verified')
        "
        :badge-type="isEmailVerified ? 'success' : 'error'"
        :label="$t('account.email')"
        :required="isModuleNeededOnFirstWithdraw('email')"
        @action="toggleEdit"
      >
        {{ userEmail }}
        <button
          v-if="!isEmailVerified && !isEditing"
          :class="$style.verifyBtn"
          @click.prevent="onSubmitEmailVerify"
        >
          {{ $t('account.email_section.click_to_verify') }}
        </button>
      </UserProfileDataCard>

      <form
        v-if="isEditing && (!isEmailVerified || isUpdateEmailAllowed)"
        :class="$style.editContent"
        @submit.prevent="handleEditEmail"
      >
        <div :class="$style.description">
          {{ $t('account.email_section.check') }}
        </div>

        <div :class="$style.formGroup">
          <BaseFormInput
            id="email"
            v-model="formEmail"
            :disabled="!isUpdateEmailAllowed"
            :initial-value="userEmail"
            name="email"
            :placeholder="$t('account.email')"
            type="email"
          />
        </div>

        <div class="flex justify-between items-end w-full col-span-2">
          <button
            v-if="!isEmailVerified && isEmailEqual"
            :class="$style.btnSubmit"
            @click.prevent="onSubmitEmailVerify"
          >
            <NuxtIcon name="check" />
            {{ $t('account.email_section.verify') }}
          </button>
          <button
            v-else
            :class="$style.btnSubmit"
            :disabled="!formEmail"
            type="submit"
          >
            <NuxtIcon name="sync" />
            {{ $t('account.email_section.change') }}
          </button>
        </div>
      </form>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-3;
}

.grow {
  --card-grow: 1;
}

.opacity {
  @apply opacity-20 pointer-events-none select-none;
}

.verifyBtn {
  @apply block text-xs text-primary mt-1 text-left;
}

.editContent {
  @apply w-full;

  .description {
    @apply text-sm text-texts/70 mb-4 max-w-[450px];
  }

  .formGroup {
    @apply mb-4 relative max-w-96;

    label {
      @apply text-xs font-medium;
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }
}
</style>
